import { isPaymentRequired } from "../helpers";
import { isPlaceholderPlate } from "./utils";

export function toBookingStatus(status) {
  const value = String(status || "").toUpperCase();

  if (value === "PENDING_ENTRY") return "awaiting";
  if (value === "CURRENT") return "allocated";

  return value.toLowerCase();
}

export function toLotInventoryItem(item) {
  const lot = item.lot || {};
  const capacity = item.capacity == null ? null : Number(item.capacity);
  const occupied = Number(item.occupied || 0);
  const allocatedTickets = Number(item.allocatedTickets || 0);
  const redeemedTickets = Number(item.redeemedTickets || 0);
  const flaggedTickets = Number(item.flaggedTickets || 0);
  const available = item.available == null ? null : Number(item.available);
  const bookings = (item.bookings || []).map((booking) => {
    const offsiteTicket = booking?.offsiteTickets?.[0];
    const rowStatus = offsiteTicket?.status || "awaiting";
    const issuedBy = offsiteTicket?.issuedBy;
    const plate = offsiteTicket?.licensePlate || booking?.plate;
    /* A chargeback card never gets a succeeded Stripe charge, so isPaid stays
     * false for the life of the booking. Reading it as unpaid would park these
     * rows behind a Take payment button that the API refuses (422) and would
     * block ticket allocation forever, so the desk treats them as settled. */
    const paymentRequired = isPaymentRequired(booking?.rateCard);
    const isPaid = Boolean(booking?.paymentState?.isPaid);

    return {
      id: booking?.id || offsiteTicket?.id,
      ref: booking?.id || offsiteTicket?.id,
      rateCardId: booking?.rateCardId,
      guestName: offsiteTicket?.guestName || booking?.guestName || "-",
      email: offsiteTicket?.guestEmail || booking?.guestEmail,
      phone: booking?.guestPhone,
      stripeCustomerId: booking?.stripeCustomerId,
      plate: isPlaceholderPlate(plate) ? null : plate,
      startDay: booking?.startDay,
      endDay: booking?.endDay,
      checkIn: booking?.startDay,
      checkOut: booking?.endDay,
      voucherId: offsiteTicket?.ticketCode,
      issuedAt: offsiteTicket?.issuedAt,
      issuedBy,
      issuedByName: issuedBy?.name || issuedBy?.email || null,
      flagNote: offsiteTicket?.flagReason,
      rateCard: booking?.rateCard ?? null,
      paymentRequired,
      paid: paymentRequired ? isPaid : true,
      /* Kept apart from `paid` so a receipt line can still say whether money
         actually moved. */
      isPaid,
      pendingCharge: paymentRequired
        ? Boolean(booking?.paymentState?.isPendingCharge)
        : false,
      amount: booking?.paymentState?.amountAud == null ? null : Number(booking.paymentState.amountAud),
      paidAtRelative: booking?.paymentState?.paidAt,
      paymentState: booking?.paymentState,
      status: toBookingStatus(rowStatus),
      hasTicket: !!offsiteTicket,
      offsiteTicket,
      booking,
    };
  });

  return {
    id: lot.id,
    name: lot.name,
    description: lot.description,
    type: lot.type,
    capacity,
    enableCapacityLimit: lot.enableCapacityLimit,
    address: lot.address,
    walkingDistanceMinutes: lot.walkingDistanceMinutes,
    occupied,
    allocatedTickets,
    redeemedTickets,
    flaggedTickets,
    available,
    occupancyPercent: item.occupancyPercent == null ? null : Number(item.occupancyPercent),
    bookings,
  };
}

export function sortLotsForStaff(a, b) {
  if (a.type !== b.type) return a.type === "ONSITE" ? -1 : 1;

  return String(a.name || "").localeCompare(String(b.name || ""));
}

/* Flattens the mapped lots into one booking list, stamping the lot each row
   belongs to - the shape `IssueDialog` and the booking tables expect. */
export function toParkingBookings(lots) {
  return lots.flatMap((lot) =>
    lot.bookings.map((booking) => ({
      ...booking,
      lotId: lot.id,
      lotName: lot.name,
      lotType: lot.type,
    })),
  );
}
